import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { PageContainer } from '@/components/common/PageContainer';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { DataTable, Column } from '@/components/common/DataTable';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { RotateCcw, Eye, RefreshCw, Plus, ChevronRight } from 'lucide-react';
import { formatCurrency, formatDate } from '@/lib/formatters';
import { RefundSettlement } from '@/types/financial.types';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { CreateRefundModal } from '@/components/features/financials/CreateRefundModal';
import { useI18n } from '@/contexts/I18nContext';
import { truncateText } from '@/lib/formatters';

const STATUS_COLOR: Record<string, string> = {
  draft: 'bg-muted text-muted-foreground border-border',
  approved: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  paid: 'bg-green-500/10 text-green-600 border-green-500/20',
  rejected: 'bg-red-500/10 text-red-600 border-red-500/20',
  reversed: 'bg-orange-500/10 text-orange-600 border-orange-500/20',
};

const DEBTOR_LABEL: Record<string, string> = {
  carrier: 'شركة شحن',
  tenant: 'تاجر',
  platform: 'المنصة',
};

export default function RefundsList() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { get, loading } = useApi();
  const [refunds, setRefunds] = useState<RefundSettlement[]>([]);
  const [hasFetched, setHasFetched] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const STATUS_LABEL: Record<string, string> = {
    draft: t.financials.status_draft,
    approved: t.financials.status_approved,
    paid: t.financials.status_paid,
    rejected: t.financials.status_rejected,
    reversed: t.financials.status_reversed,
  };

  useEffect(() => {
    fetchRefunds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRefunds = async () => {
    setHasFetched(false);
    try {
      const result: any = await get('/api/v1/refunds');
      const data = result?.success !== undefined ? result?.data : result;
      const list = Array.isArray(data) ? data : data?.list ?? data?.data ?? [];
      setRefunds(list);
    } catch {
      toast.error('فشل في تحميل المرتجعات');
    } finally {
      setHasFetched(true);
    }
  };

  const columns: Column<RefundSettlement>[] = [
    {
      header: 'تاريخ الإنشاء',
      accessor: (row) => (
        <span className="text-sm text-muted-foreground">{formatDate(row.created_at)}</span>
      ),
      className: 'min-w-[120px]',
    },
    {
      header: 'المدين',
      accessor: (row) => (
        <div>
          <div className="text-sm font-medium">{DEBTOR_LABEL[row.debtor_type] ?? row.debtor_type}</div>
          <div className="text-xs text-muted-foreground font-mono" dir="ltr">
            {truncateText(row.debtor_id, 12)}
          </div>
        </div>
      ),
      className: 'min-w-[140px]',
    },
    {
      header: 'الشحنة',
      accessor: (row) => (
        <span className="text-sm font-mono" dir="ltr">
          {row.shipment_id ? truncateText(row.shipment_id, 10) : '—'}
        </span>
      ),
      className: 'min-w-[100px]',
    },
    {
      header: t.financials.customer_name,
      accessor: (row) => (
        <span className="text-sm">
          {row.customer_metadata?.name ?? '—'}
        </span>
      ),
      className: 'min-w-[140px]',
    },
    {
      header: t.financials.amount,
      accessor: (row) => (
        <span className="font-bold text-primary">{formatCurrency(row.amount)}</span>
      ),
      className: 'min-w-[120px]',
    },
    {
      header: 'الحالة',
      accessor: (row) => (
        <Badge className={cn('border text-xs', STATUS_COLOR[row.status])}>
          {STATUS_LABEL[row.status] ?? row.status}
        </Badge>
      ),
      className: 'min-w-[100px]',
    },
    {
      header: t.financials.refund_reason,
      accessor: (row) => (
        <span className="text-sm text-muted-foreground" title={row.reason}>
          {truncateText(row.reason ?? '', 40)}
        </span>
      ),
      className: 'min-w-[180px]',
    },
  ];

  return (
    <PageContainer>
      <div className="mb-4 md:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ChevronRight className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl md:text-2xl font-bold">{t.financials.refunds}</h1>
              <p className="text-sm text-muted-foreground">طلبات استرجاع المبالغ</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchRefunds} disabled={loading} className="gap-2">
              <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
              تحديث
            </Button>
            <Button onClick={() => setShowCreateModal(true)} className="gap-2">
              <Plus className="h-4 w-4" />
              {t.financials.create_refund}
            </Button>
          </div>
        </div>
      </div>

      {!hasFetched ? (
        <LoadingSkeleton type="table" rows={8} />
      ) : refunds.length === 0 ? (
        <EmptyState
          icon={<RotateCcw className="h-12 w-12" />}
          title={t.financials.no_refunds_pending}
          description="لم يتم العثور على مرتجعات"
        />
      ) : (
        <DataTable
          data={refunds}
          columns={columns}
          actions={(row) => (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => navigate(`/dashboard/refunds/${row.id}`)}
              title="عرض التفاصيل"
            >
              <Eye className="h-4 w-4" />
            </Button>
          )}
          emptyMessage={t.financials.no_refunds_pending}
        />
      )}

      <CreateRefundModal
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
        onSuccess={fetchRefunds}
      />
    </PageContainer>
  );
}
